import React, { useState, useEffect } from 'react';
import { Search, Plus, FileCheck, User, ChevronRight, Edit2 } from 'lucide-react';
import AddMockExamModal from './Modals/AddMockExamModal';
import AddTestPrepStudentModal from './Modals/AddTestPrepStudentModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Student {
    id: string;
    name: string;
    phone?: string;
    school?: string;
    grade?: string;
}

interface MockExam {
    id: string;
    exam_date: string;
    exam_type: string;
    score_total: number;
    score_details: Record<string, number>;
    notes: string;
}

const MockExams: React.FC = () => {
    const { user } = useAuth();
    const [showAddModal, setShowAddModal] = useState(false);
    const [showAddStudentModal, setShowAddStudentModal] = useState(false);

    const [students, setStudents] = useState<Student[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [examResults, setExamResults] = useState<MockExam[]>([]);
    const [loadingExams, setLoadingExams] = useState(false);
    const [selectedExam, setSelectedExam] = useState<MockExam | null>(null);

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        const { data, error } = await supabase
            .from('testprep_students')
            .select('id, first_name, last_name, phone, school, grade')
            .order('first_name');
        if (!error && data) {
            setStudents(data.map((s: any) => ({
                id: s.id,
                name: `${s.first_name} ${s.last_name}`,
                phone: s.phone || '',
                school: s.school || '',
                grade: s.grade || ''
            })));
        }
    };

    useEffect(() => {
        if (selectedStudent) {
            fetchStudentExams(selectedStudent.id);
        } else {
            setExamResults([]);
        }
    }, [selectedStudent]);

    const fetchStudentExams = async (studentId: string) => {
        setLoadingExams(true);
        try {
            const { data, error } = await supabase
                .from('testprep_mock_exams')
                .select('*')
                .eq('student_id', studentId)
                .order('exam_date', { ascending: false });

            if (error) throw error;
            setExamResults(data || []);
        } catch (error) {
            console.error('Error fetching exams:', error);
        } finally {
            setLoadingExams(false);
        }
    };

    const handleStudentAdded = (student: any) => {
        fetchStudents();
        setShowAddStudentModal(false);
    };

    const handleEdit = (exam: MockExam) => {
        setSelectedExam(exam);
        setShowAddModal(true);
    };

    const handleAddClick = () => {
        setSelectedExam(null);
        setShowAddModal(true);
    };

    const filteredStudents = students.filter(student =>
        student.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex h-[calc(100vh-6rem)] gap-6">
            {/* Left Sidebar - Student List */}
            <div className="w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                    <h3 className="font-bold text-gray-900 dark:text-white">Öğrenciler</h3>
                    <button
                        onClick={() => setShowAddStudentModal(true)}
                        className="flex items-center space-x-1 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-blue-600 px-2 py-1 rounded-md transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Yeni Ekle</span>
                    </button>
                </div>
                <div className="p-4 pt-2 border-b border-gray-200 dark:border-gray-800">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Öğrenci ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {filteredStudents.length > 0 ? (
                        filteredStudents.map((student) => (
                            <button
                                key={student.id}
                                onClick={() => setSelectedStudent(student)}
                                className={`w-full flex items-center p-3 rounded-lg text-left transition-colors ${selectedStudent?.id === student.id
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                                    } `}
                            >
                                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-3">
                                    <User className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{student.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{student.school || student.grade}</p>
                                </div>
                                <ChevronRight className={`w-4 h-4 ${selectedStudent?.id === student.id ? 'opacity-100' : 'opacity-0'} `} />
                            </button>
                        ))
                    ) : (
                        <div className="p-4 text-center text-sm text-gray-500">
                            {students.length === 0 ? "Henüz kayıtlı öğrenci yok." : "Öğrenci bulunamadı."}
                            {students.length === 0 && (
                                <button
                                    onClick={() => setShowAddStudentModal(true)}
                                    className="mt-2 text-blue-600 hover:text-blue-700 font-medium text-xs block mx-auto"
                                >
                                    + Şimdi Ekle
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content - Exam Results */}
            <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
                <div className="flex items-center justify-between">
                    <div>
                        {selectedStudent ? (
                            <>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedStudent.name}</h2>
                                <p className="text-gray-600 dark:text-gray-400">Deneme sınavı sonuçları ve gelişimi</p>
                            </>
                        ) : (
                            <>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Deneme Sınavları</h2>
                                <p className="text-gray-600 dark:text-gray-400">Sonuçları görüntülemek için soldan öğrenci seçin.</p>
                            </>
                        )}
                    </div>
                    <button
                        disabled={!selectedStudent}
                        onClick={handleAddClick}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Yeni Sınav Sonucu Ekle</span>
                    </button>
                </div>

                {selectedStudent ? (
                    <div className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden flex flex-col">
                        {loadingExams ? (
                            <div className="flex-1 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : examResults.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınav Türü</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam Puan</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detaylar</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notlar</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {examResults.map((exam) => (
                                            <tr key={exam.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {new Date(exam.exam_date).toLocaleDateString('tr-TR')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                                                        {exam.exam_type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white">
                                                    {exam.score_total}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                                                    <div className="flex flex-wrap gap-1">
                                                        {Object.entries(exam.score_details || {}).map(([key, value]) => (
                                                            <span key={key} className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700">
                                                                {key}: {value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={exam.notes}>
                                                    {exam.notes || '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleEdit(exam)}
                                                        className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400 p-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                                        title="Düzenle"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                    <FileCheck className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sonuç Bulunamadı</h3>
                                <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                                    Bu öğrenciye ait kayıtlı deneme sınavı sonucu bulunmuyor.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 bg-gray-50 dark:bg-gray-900/50 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex items-center justify-center text-center p-8">
                        <div className="max-w-md">
                            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Öğrenci Seçin</h3>
                            <p className="text-gray-500 dark:text-gray-400">
                                Sınav sonuçlarını görüntülemek için soldaki listeden bir öğrenci seçiniz veya yeni öğrenci ekleyiniz.
                            </p>
                            <button
                                onClick={() => setShowAddStudentModal(true)}
                                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                            >
                                Öğrenci Listesini Düzenle (Ekle)
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <AddMockExamModal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setSelectedExam(null);
                }}
                selectedStudentId={selectedStudent?.id}
                initialData={selectedExam}
                onSave={() => {
                    if (selectedStudent) {
                        fetchStudentExams(selectedStudent.id);
                    }
                }}
            />

            <AddTestPrepStudentModal
                isOpen={showAddStudentModal}
                onClose={() => setShowAddStudentModal(false)}
                onSave={handleStudentAdded}
            />
        </div>
    );
};

export default MockExams;
